import { Play, Pause, Volume2, Radio, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Hls from "hls.js";
import { useApi } from "../../lib/api";
import { useRadio } from "../../context/RadioContext";

export const playLive = (a, url, hlsRef, onErr) => {
  const live = `${url}/playlist.m3u8`;
  hlsRef.current?.destroy();
  hlsRef.current = null;
  if (Hls.isSupported()) {
    const h = new Hls({ liveSyncDurationCount: 1, maxBufferLength: 8, startFragPrefetch: true, manifestLoadingMaxRetry: 1, levelLoadingMaxRetry: 1, fragLoadingMaxRetry: 2 });
    h.on(Hls.Events.ERROR, (_, d) => { if (d.fatal) { h.destroy(); hlsRef.current = null; a.src = url; a.play().catch(onErr); } });
    h.loadSource(live);
    h.attachMedia(a);
    hlsRef.current = h;
  } else {
    a.src = a.canPlayType("application/vnd.apple.mpegurl") ? live : url;
  }
  return a.play();
};

export const SLUG = { peace: "peace-fm", okay: "okay-fm", neat: "neat-fm", hello: "hello-fm", utv: "utv" };
const MIN = 88, MAX = 108;
const pos = (f) => ((parseFloat(f) - MIN) / (MAX - MIN)) * 100;

const Dial = ({ stations, current, onTune }) => (
  <div className="relative h-28 rounded-md overflow-hidden border-2 border-[#3a2a08] bg-[#1b1305]" data-testid="fm-dial">
    <div className="absolute inset-0 opacity-25 gold-foil" />
    <div className="absolute inset-x-6 top-4 bottom-8">
      {Array.from({ length: 41 }).map((_, i) => (
        <span key={i} className="absolute bottom-0 w-px bg-[#f7e08f]/70" style={{ left: `${(i / 40) * 100}%`, height: i % 4 === 0 ? "40%" : "20%" }} />
      ))}
      {[88, 92, 96, 100, 104, 108].map((f) => (
        <span key={f} className="absolute -bottom-6 -translate-x-1/2 font-data text-[10px] text-[#f7e08f]" style={{ left: `${pos(f)}%`, height: undefined }}>{f}</span>
      ))}
      {[...stations].sort((a, b) => parseFloat(a.freq) - parseFloat(b.freq)).map((s, i) => (
        <button key={s.key} onClick={() => onTune(s)} className={`absolute -translate-x-1/2 font-data text-[9px] px-1.5 py-0.5 rounded transition-colors whitespace-nowrap ${current.key === s.key ? "bg-[#f7e08f] text-[#1b1305] z-10" : "text-[#f7e08f]/80 hover:text-white bg-[#1b1305]/70"}`} style={{ left: `${pos(s.freq)}%`, top: `${(i % 3) * 16 - 4}px` }} data-testid={`dial-station-${s.key}`}>
          {s.name.replace(" FM", "")}
        </button>
      ))}
      <span className="absolute -top-1 bottom-0 w-0.5 bg-red-500 shadow-[0_0_10px_#ef4444] transition-all duration-700" style={{ left: `${pos(current.freq)}%` }} data-testid="dial-needle" />
    </div>
  </div>
);

export const RadioPlayer = ({ stationKey }) => {
  const { data } = useApi("/public/stations");
  const nav = useNavigate();
  const radio = useRadio();
  const fm = (data || []).filter((s) => s.audio_url);
  const current = fm.find((s) => s.key === stationKey);
  if (!current) return null;
  const mine = radio.station?.key === current.key;
  const playing = mine && radio.state === "playing";
  const busy = mine && radio.state === "loading";
  const err = mine && radio.state === "error" ? `${current.name}'s official stream is offline at source right now — please try again shortly.` : "";
  const toggle = () => (playing || busy ? radio.stop() : radio.play(current));
  const onTune = (s) => {
    const on = radio.state === "playing" || radio.state === "loading";
    nav(`/entities/${SLUG[s.key]}`);
    if (on) radio.play(s);
  };
  return (
    <div className="rounded-lg p-5 bg-[#0e0b04] gold-frame-lg shadow-2xl" data-testid="radio-player">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-[#f7e08f]"><Radio size={16} /><span className="font-data text-xs uppercase tracking-widest">{busy ? "Tuning in…" : playing ? "On air · live" : "Tune in"}</span>{playing && <span className="live-dot" />}</div>
        <div className="font-display text-2xl font-bold gold-text" data-testid="player-frequency">{current.freq} FM</div>
      </div>
      <Dial stations={fm} current={current} onTune={onTune} />
      <div className="mt-8 flex items-center gap-5">
        <button onClick={toggle} className="w-16 h-16 rounded-full gold-foil flex items-center justify-center shadow-[0_0_24px_rgba(247,224,143,.45)] hover:scale-105 transition-transform" data-testid="player-play-btn" aria-label={playing ? "Pause" : "Play live"}>
          {busy ? <Loader2 size={26} className="text-[#1b1305] animate-spin" /> : playing ? <Pause size={26} className="text-[#1b1305]" /> : <Play size={26} className="text-[#1b1305] ml-1" />}
        </button>
        <div className="flex-1">
          <div className="text-white font-semibold">{current.name} · {current.city}</div>
          <div className="flex items-center gap-2 mt-2"><Volume2 size={14} className="text-[#f7e08f]" />
            <input type="range" min="0" max="1" step="0.05" value={radio.volume} onChange={(e) => radio.setVolume(Number(e.target.value))} className="w-full accent-[#d4af37]" data-testid="player-volume" />
          </div>
        </div>
      </div>
      {err && <p className="text-xs text-red-300 mt-3" data-testid="player-error">{err}</p>}
      <p className="font-data text-[9px] text-white/40 mt-3 uppercase tracking-wider">Official stream via Atunwa Digital / StreamGuys</p>
    </div>
  );
};

export const TvPlayer = ({ station }) => (
  <div className="rounded-lg p-4 bg-[#0e0b04] gold-frame-lg shadow-2xl" data-testid="tv-player">
    <div className="flex items-center justify-between mb-3 text-[#f7e08f]">
      <span className="flex items-center gap-2 font-data text-xs uppercase tracking-widest"><span className="live-dot" /> Live TV</span>
      <div className="flex gap-1" data-testid="tv-channel-dial">
        {["CH", "UTV"].map((c, i) => <span key={c} className={`font-data text-[10px] px-2 py-0.5 rounded ${i ? "bg-[#f7e08f] text-[#1b1305]" : "text-[#f7e08f]/60"}`}>{c}</span>)}
      </div>
    </div>
    <div className="aspect-video rounded overflow-hidden bg-black">
      <iframe title={`${station.name} live`} src={station.tv_embed} className="w-full h-full border-0" allow="autoplay; fullscreen; encrypted-media" allowFullScreen data-testid="tv-iframe" />
    </div>
    <p className="font-data text-[9px] text-white/40 mt-2 uppercase tracking-wider">Official UTV live player via Atunwa Digital</p>
  </div>
);
